import { useCallback, useEffect, useRef, useState } from "react";
import type { MultiSelectOption, MultiSelectValue } from "./types";

interface UseMultiSelectProps {
  options: MultiSelectOption[];
  value?: MultiSelectValue;
  onChange?: (values: MultiSelectValue) => void;
}

interface MultiSelectState {
  values: MultiSelectValue;
  selectedItems: MultiSelectOption[];
  isOpen: boolean;
}

const initialState: MultiSelectState = {
  values: [],
  selectedItems: [],
  isOpen: false,
};

export const useMultiSelect = ({
  options,
  value = [],
  onChange,
}: UseMultiSelectProps) => {
  const [state, setState] = useState<MultiSelectState>(initialState);
  const optionsRef = useRef(options);

  useEffect(() => {
    optionsRef.current = options;
  }, [options]);

  // Sync value prop with internal state
  useEffect(() => {
    const selectedItems = options.filter((opt) => value.includes(opt.value));

    setState((prevState) => ({
      ...prevState,
      values: value,
      selectedItems,
    }));
  }, [options, value]);

  const onChoose = useCallback(
    (selectedIds: string[]) => {
      // Extract values that correspond to the selected IDs
      const newValues = optionsRef.current
        .filter((opt) => selectedIds.includes(String(opt.value)))
        .map((opt) => opt.value);

      const newSelectedItems = optionsRef.current.filter((opt) =>
        newValues.includes(opt.value)
      );

      setState((prevState) => ({
        ...prevState,
        values: newValues,
        selectedItems: newSelectedItems,
      }));

      onChange?.(newValues);
    },
    [onChange]
  );

  const onRemove = useCallback(
    (value: string | number, event?: React.MouseEvent) => {
      event?.stopPropagation();

      setState((prev) => {
        const newValues = prev.values.filter((v) => v !== value);
        const newSelectedItems = optionsRef.current.filter((opt) =>
          newValues.includes(opt.value)
        );
        onChange?.(newValues);
        return {
          ...prev,
          values: newValues,
          selectedItems: newSelectedItems,
        };
      });
    },
    [onChange]
  );

  const onRemoveAll = useCallback(() => {
    setState((prevState) => ({
      ...prevState,
      values: [],
      selectedItems: [],
    }));

    onChange?.([]);
  }, [onChange]);

  const onSelectClick = useCallback(() => {
    setState((prevState) => ({
      ...prevState,
      isOpen: !prevState.isOpen,
    }));
  }, []);

  const onClose = useCallback(() => {
    setState((prevState) => ({
      ...prevState,
      isOpen: false,
    }));
  }, []);

  return {
    values: state.values,
    stateList: options,
    selectedItems: state.selectedItems,
    isOpen: state.isOpen,
    onChoose,
    onSelectClick,
    onRemove,
    onRemoveAll,
    onClose,
  };
};
